// @ts-strict-ignore
/* eslint-disable react-refresh/only-export-components */
import * as React from 'react';
import {
  cloneElement,
  Component,
  createContext,
  createElement,
  createRef,
  CSSProperties,
  forwardRef,
  Fragment,
  MutableRefObject,
  PropsWithChildren,
  PureComponent,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {AttrSchema, Component as PanelComponent} from 'panel';
import WebComponent from 'webcomponent';
import {customAlphabet} from 'nanoid';

import type {Subscribable} from 'common/types/context/subscribable';
import type {IronContextRegistry} from 'common/report/context-registry';

type RefType = PanelComponent<any> | HTMLElement | null;

const HandlersToLowercaseLikeReact = [
  `onMouseEnter`,
  `onMouseLeave`,
  `onMouseOut`,
  `onPointerDown`,
  `onPointerMove`,
  `onPointerUp`,
  `onPointerCancel`,
  `onLostPointerCapture`,
];

/**
 * Convert camelCase to kebab-case. Works a bit differently than lodash's kebabCase by splitting only on capital letters.
 */
function kebabCase(str: string) {
  return str
    .replace(/([a-z])([A-Z])/g, `$1-$2`)
    .replace(/[\s_]+/g, `-`)
    .toLowerCase();
}

type KebabCaseToCamelCase<S extends string> = S extends `${infer T}-${infer U}`
  ? `${T}${Capitalize<KebabCaseToCamelCase<U>>}`
  : S;

/**
 * Convert kebab-case to camelCase. Works a bit differently than lodash's camelCase by only capitalizing after the hyphen.
 * This must match the Typescript implementation above.
 */
function camelCase(str: string) {
  // turn kebab-case into camelCase, making sure to lowercase the first letter of the string
  const camel = str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

  // lowercase the first letter
  return camel.charAt(0).toLowerCase() + camel.slice(1);
}

/**
 * customize fork from https://github.com/BBKolton/reactify-wc/blob/master/src/index.ts
 * we support panel params usage and attrSchema declaration
 *
 * @condition one of following:
 *  1. customElement.define(tagName, component)
 *  2. defineAndMakeJsxFactory(tagName, component)
 *  must be called before using this function
 *
 * @example
 * const MyComponent = wc2react('my-component', Button);
 * @deprecated Don't use this directly except in very specific cases and you know what you're doing. Use `panel2react` instead.
 */
export function wc2react<Props>(tagName: string, component: typeof PanelComponent | typeof WebComponent) {
  // make attr and params set available static time to avoid runtime cost
  const attrs = (component as typeof PanelComponent).attrsSchema
    ? new Set(Object.keys((component as typeof PanelComponent).attrsSchema))
    : new Set<string>();
  const params = (component as typeof PanelComponent).prototype.config?.params
    ? new Set(Object.keys((component as typeof PanelComponent).prototype.config.params))
    : new Set<string>();

  let defaultProps = {};
  if ((component as typeof PanelComponent).prototype.config?.params) {
    const defaultParams = (component as typeof PanelComponent).prototype.config.defaultParams || {};
    defaultProps = {...defaultProps, ...defaultParams};
  }
  if ((component as typeof PanelComponent).attrsSchema) {
    const attrsSchema = (component as typeof PanelComponent).attrsSchema;
    const defaultAttrs = Object.fromEntries(
      Object.keys(attrsSchema)
        .filter((key) => typeof attrsSchema[key] !== `string`)
        .filter((key) => (attrsSchema[key] as AttrSchema).default)
        .map((key) => [camelCase(key), (attrsSchema[key] as AttrSchema).default]),
    );
    defaultProps = {...defaultProps, ...defaultAttrs};
  }
  type ReactifiedProps = PropsWithChildren<Props> & {
    innerRef?: MutableRefObject<RefType> | ((instance?: RefType) => void);
    style?: CSSProperties;
    className?: string;
    elref?: string;
    slot?: string;
  };
  class Reactified extends Component<ReactifiedProps> {
    declare eventHandlers: Array<[string, (...args: any[]) => any]>;
    declare attrs: Set<string>;
    declare params: Set<string>;
    declare ref: MutableRefObject<RefType>;
    declare reactWrapperId: string;

    static displayName = `${tagName}-wrapper`;

    constructor(props) {
      super(props);

      const reactWrapperId = getReactComponentId();

      this.eventHandlers = [];
      this.ref = createRef<RefType>();
      this.attrs = attrs;
      this.params = params;
      this.reactWrapperId = reactWrapperId;

      document.addEventListener(`preComponentInitialized`, this.onComponentInitialized);
    }

    /**
     * Panel emits a preComponentInitialized event during its connectedCallback right before the very first render.
     * This allows us to imitate the Panel lifecycle by setting params & event listeners before a component's connectedCallback is run.
     *
     * This is what the lifecycle looks like:
     * - On render(), attributes are set on the component synchronously
     * - ReactDOM creates the HTML element using document.createElement, which invokes the Web Component `constructor`
     * - ReactDOM mounts the component to the DOM
     * - The browser calls the `connectedCallback` method since it is a Custom Element
     * - The component is expected to call super.connectedCallback(), which invokes PanelComponent's connectedCallback
     * - PanelComponent's connectedCallback emits a preComponentInitialized event (THIS HANDLER HERE)
     * - The rest of the component's connectedCallback runs, with params, attrs, and event listeners properly set
     * - componentDidMount runs in React to sync the rest of the component's state
     */
    onComponentInitialized = (ev: CustomEvent) => {
      const el = ev.detail.el as RefType;

      if (el.getAttribute(`rId`) === this.reactWrapperId) {
        this.setRef(el);
        this.update(undefined, false);
      }
    };

    componentDidMount() {
      // we run update again on mount in order to make sure props are set because we don't set them during preComponentInitialized.
      // ideally we would, but for some reason this breaks some components (like grid-layout)
      this.update();
    }

    componentDidUpdate(prevProps: ReactifiedProps) {
      this.update(prevProps);
    }

    componentWillUnmount() {
      this.clearEventHandlers();
      document.removeEventListener(`preComponentInitialized`, this.onComponentInitialized);
    }

    setRef = (instance: RefType) => {
      if (this.props.innerRef && typeof this.props.innerRef === `function`) {
        this.props.innerRef(instance);
      } else if (this.props.innerRef && typeof this.props.innerRef === `object`) {
        this.props.innerRef.current = instance;
      }
      this.ref.current = instance;
    };

    /** Separates props into Panel parts. */
    getPanelProps() {
      const panelAttrs: string[] = [];
      const panelParams: string[] = [];
      const panelProps: string[] = [];
      const panelEventHandlers: string[] = [];

      Object.entries(this.props).forEach(([prop]) => {
        // ignore these, they will be handled by react
        if ([`className`, `style`, `children`, `slot`].includes(prop)) {
          return;
        }

        // params
        if (this.params.has(prop)) {
          panelParams.push(prop);
          return;
        }

        // attributes
        if (component.observedAttributes?.includes(kebabCase(prop)) || [`styleOverride`, `elref`].includes(prop)) {
          panelAttrs.push(prop);
          return;
        }

        // event handlers
        if (prop.match(/^on[A-Z]/)) {
          panelEventHandlers.push(prop);
          return;
        }

        // all the rest are props
        panelProps.push(prop);
      });

      return {
        attrs: panelAttrs,
        params: panelParams,
        props: panelProps,
        eventHandlers: panelEventHandlers,
      };
    }

    update(prevProps?: ReactifiedProps, syncProps = true) {
      const {params: panelParams, props: panelProps, eventHandlers: panelEventHandlers} = this.getPanelProps();

      this.clearEventHandlers();

      // make sure event listeners are attached first
      // some of the setters will trigger events
      panelEventHandlers.forEach((propName) => {
        const eventName = propName.slice(2);
        const handler = this.props[propName];
        const addEvent = (name: string) => {
          this.eventHandlers.push([name, handler]);
          this.ref.current.addEventListener(name, handler);
        };

        if (typeof handler === `function`) {
          if (HandlersToLowercaseLikeReact.includes(propName)) {
            addEvent(eventName.toLowerCase());
          } else {
            // Just be extra cautious here, we have a mix of `kebab-case` and `camelCase` event names
            addEvent(kebabCase(eventName));
            addEvent(camelCase(eventName));
          }
        }
      });

      // set params
      const paramObj = Object.fromEntries(panelParams.map((param) => [param, this.props[param]]));
      if (this.ref.current instanceof PanelComponent) {
        this.ref.current.setParams(paramObj);
      }

      // set props
      if (syncProps) {
        panelProps.forEach((propName) => {
          const value = this.props[propName];
          // for some compatibility with snabbdom, check strict equal for prevProp and current prop
          // https://github.com/snabbdom/snabbdom/blob/v3.0.3/src/modules/props.ts#L15
          if (prevProps?.[propName] !== value) {
            this.ref.current[propName] = value;
          }
        });
      }
    }

    clearEventHandlers() {
      this.eventHandlers.forEach(([event, handler]) => {
        this.ref.current.removeEventListener(event, handler as EventListener);
      });
      this.eventHandlers = [];
    }

    render() {
      const {children, style, elref, slot, className} = this.props;

      const {attrs: panelAttrs} = this.getPanelProps();
      const attrObj = Object.fromEntries(
        panelAttrs
          .map((attr) => [kebabCase(attr), this.props[attr]])
          .filter(([_, value]) => value !== undefined && value !== null && value !== false)
          .map(([name, value]) => [name, typeof value === `object` ? JSON.stringify(value) : value]),
      );

      return createElement(
        tagName,
        {
          ref: this.setRef,
          style,
          elref,
          slot,
          class: className,
          rId: this.reactWrapperId,
          ...attrObj,
        },
        children,
      );
    }
  }

  const Comp = forwardRef<{innerRef?: MutableRefObject<RefType>}, PropsWithChildren<Props> & {style?: CSSProperties}>(
    ({children, ...props}, ref) => createElement(Reactified, {innerRef: ref, ...props}, children),
  );
  Comp.displayName = `${tagName}-forward-ref`;
  Comp.defaultProps = defaultProps;
  return Comp;
}

export function contextify<S extends Subscribable>(): {
  Provider: React.ComponentType<React.ProviderProps<S>>;
  Consumer: React.Consumer<S>;
  hook: () => S;
} {
  const context = createContext<S>(null);
  class Provider extends PureComponent<{children?: ReactNode; value: S}> {
    componentDidMount() {
      this.props.value.subscribe(this.update);
    }

    componentWillUnmount() {
      (this.props.value as S).unsubscribe?.(this.update);
    }

    update = () => {
      this.forceUpdate();
    };

    render() {
      // create a new Proxy on each render so that the consumed value always triggers a prop update when passed into a PureComponent
      return <context.Provider value={new Proxy(this.props.value, {})}>{this.props.children}</context.Provider>;
    }
  }

  const Consumer = context.Consumer;

  return {
    Provider,
    Consumer,
    hook: () => useContext(context),
  };
}

function isSubscribable(context: any): context is Subscribable {
  return typeof context.subscribe === `function` && typeof context.unsubscribe === `function`;
}

/**
 * Creates a hook that subscribes directly to a root context. React Providers will be ignored.
 * The context must implement `Subscribable` in order to cause re-renders.
 */
export function createRootContextHook<T extends keyof IronContextRegistry>(
  contextName: T,
): () => IronContextRegistry[T] {
  return () => {
    // grabbing the context root during first render because we know it will be available by now
    // we're also assuming that the context is a stable reference
    const context = useMemo(() => {
      const contextRoot: PanelComponent<EmptyObj> =
        document.querySelector(`mp-browser-context-root`) ?? document.querySelector(`mp-test-context-root`);
      return contextRoot.getConfig(`defaultContexts`)[contextName as any];
    }, []);

    if (!context) {
      throw new Error(`Trying to subscribe to a root context that doesn't exist: ${contextName}`);
    }

    const [snapshot, setSnapshot] = useState(context);

    useEffect(() => {
      const subscription = () => {
        setSnapshot(new Proxy(context, {}));
      };

      if (isSubscribable(context)) {
        context.subscribe(subscription);
      }

      return () => {
        if (isSubscribable(context)) {
          context.unsubscribe(subscription);
        }
      };
    }, [context]);

    return snapshot;
  };
}

// Stop gap for legacy store context to be consumed directly with hooks
export function createLegacyStoreContextHook<T extends Subscribable>(context: T): () => T {
  return () => {
    const [snapshot, setSnapshot] = useState(context);

    useEffect(() => {
      const subscription = () => {
        setSnapshot(new Proxy(context, {}));
      };

      if (isSubscribable(context)) {
        context.subscribe(subscription);
      }

      return () => {
        if (isSubscribable(context)) {
          context.unsubscribe(subscription);
        }
      };
    }, []);

    return snapshot;
  };
}

type CamelCaseKeys<T> = {[K in keyof T as KebabCaseToCamelCase<string & K>]: T[K]};

type PrefixEventHandlers<T> = {[K in keyof T as `on${Capitalize<KebabCaseToCamelCase<string & K>>}`]?: T[K]};

type Func = (...args: any[]) => any;

export type Props<
  T extends {
    attrs?: Record<string, any>;
    props?: Record<string, any>;
    params?: Record<string, any>;
    on?: Record<string, Func>;
  },
> = CamelCaseKeys<T[`attrs`]> &
  T[`props`] &
  T[`params`] &
  PrefixEventHandlers<T[`on`]> &
  Partial<{
    elref: string;
    'style-override': string;
    style: CSSProperties;
    className: string;
    innerRef: MutableRefObject<RefType>;
  }>;

export type ToReactProps<T> = Props<T> &
  Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, keyof Props<T>>;

type ArgumentTypes<F extends Func> = F extends (...args: infer A) => any ? A : never;

/**
 * Convenient helper to omit `ToReactProps` for the generic type definition.
 *
 * @see wc2react for more details
 */
export function panel2react<JsxProps>(...args: ArgumentTypes<typeof wc2react>) {
  return wc2react<ToReactProps<JsxProps>>(...args);
}

// export this for our custom consumer wrappers for hook-base API
export type ConsumerLike<T> = (props: ConsumerProps<T>) => React.ReactNode;
type ConsumerProps<T> = {children: (parameter: T) => React.ReactElement};

export function Providers({providers, children}: {providers: ReactElement[]; children: ReactNode}) {
  return (
    <Fragment>
      {providers.reduceRight((accChildren, provider) => cloneElement(provider, {}, accChildren), children)}
    </Fragment>
  );
}

const nanoid = customAlphabet(`1234567890abcdefghijklmnopqrstuvwxyz`, 6);

/**
 * Creates a new unique ID for a Reactified component.
 */
export function getReactComponentId() {
  return nanoid();
}

/**
 * Makes sure the styles are available for the component if any parent is using the shadow dom.
 */
export function withStyles<ComponentProps>(component: React.ComponentType<ComponentProps>, css: string) {
  const componentId = getReactComponentId();

  const WrappedComponent = component;

  const render = React.forwardRef((props: ComponentProps, ref) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [ready, setReady] = React.useState(false);

    React.useEffect(() => {
      const rootNode = wrapperRef.current.getRootNode();
      const styleRoot = rootNode instanceof ShadowRoot ? rootNode : document.head;

      const styleId = `react-${componentId}`;

      // make sure style doesn't already exist at this level
      const styleExists = Array.from(styleRoot.children).some((el) => el.id === styleId);
      if (!styleExists) {
        const style = document.createElement(`style`);
        style.id = styleId;
        style.textContent = css;
        styleRoot.prepend(style);
      }

      setReady(true);
    }, []);

    return (
      <div ref={wrapperRef} style={{display: `contents`}}>
        {ready && <WrappedComponent ref={ref} {...props} />}
      </div>
    );
  });
  (render as any).componentId = componentId;

  return render;
}
